import isInViewport from "../../../src/scripts/helpers/is-in-viewport";

export default class TeaserSlider {
	static initialized = new WeakSet();

	static init() {
		document.querySelectorAll('.teaser-slider').forEach(element => {
			if (TeaserSlider.initialized.has(element)) return;
			TeaserSlider.initialized.add(element);
			new TeaserSlider(element);
		});
	}

	constructor(element) {
		this.element = element;
		this.slides = [...element.querySelectorAll('.glide__slide')];
		// AwardsSlider owns its controls; retain the shared entrance animation.
		if (element.classList.contains('awards-slider')) {
			window.addEventListener('scroll', () => this.onScroll(), {passive: true});
			this.onScroll();
			return;
		}
		this.track = element.querySelector('.glide__track');
		this.list = element.querySelector('.glide__slides');
		this.bar = element.querySelector('.glide__bar');
		this.thumb = element.querySelector('.glide__bar-thumb');
		this.previous = element.querySelector('.glide__arrow--left');
		this.next = element.querySelector('.glide__arrow--right');
		if (!this.track || !this.slides.length) return;

		let options = {
			peek: {before: 0, after: 134},
			gap: 0,
			perView: 3.5,
			breakpoints: {
				1919: {
					perView: 3.5
				},
				1599: {
					perView: 2.75
				},
				1439: {
					perView: 2.5
				},
				1279: {
					perView: 2
				},
				1023: {
					perView: 1.5
				},
				767: {
					perView: 1.15,
					peek: {before: 0, after: 24},
				}
			}
		};

		if (element.dataset.perView && JSON.parse(element.dataset.perView)) {
			options = {...options, ...JSON.parse(element.dataset.perView)};
		}

		this.options = options;
		element.classList.add('teaser-slider--scroll');
		this.bar.setAttribute('role', 'slider');
		this.bar.setAttribute('tabindex', '0');
		this.bar.setAttribute('aria-valuemin', '0');
		this.bar.setAttribute('aria-valuemax', '100');
		this.bar.setAttribute('aria-orientation', 'horizontal');
		this.previous.addEventListener('click', () => this.step(-1));
		this.next.addEventListener('click', () => this.step(1));
		this.track.addEventListener('scroll', () => {
			this.update();
			this.scheduleSnap();
		}, {passive: true});
		this.track.addEventListener('pointerdown', () => {
			this.isDragging = true;
			this.cancelSnap();
		}, {passive: true});
		const release = () => {
			this.isDragging = false;
			this.scheduleSnap();
		};
		this.track.addEventListener('pointerup', release);
		this.track.addEventListener('pointercancel', release);
		this.bar.addEventListener('keydown', event => {
			const positions = {Home: 0, End: this.maxScroll, ArrowLeft: this.track.scrollLeft - this.slideStep,
				ArrowRight: this.track.scrollLeft + this.slideStep};
			if (!(event.key in positions)) return;
			event.preventDefault();
			this.scrollTo(positions[event.key], true);
		});
		this.bindDrag(this.bar, true);
		this.bindDrag(this.track, false);
		this.resizeObserver = new ResizeObserver(() => this.layout());
		this.resizeObserver.observe(element);
		this.resizeObserver.observe(element.querySelector('.glide__wrapper'));
		window.addEventListener('resize', () => this.layout(), {passive: true});
		window.addEventListener('scroll', () => this.onScroll(), {passive: true});
		this.layout();
	}

	get maxScroll() {
		return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
	}

	layout() {
		this.cancelSnap();
		const breakpoint = Object.keys(this.options.breakpoints).map(Number).sort((a, b) => a - b)
			.find(width => window.matchMedia(`(max-width: ${width}px)`).matches);
		const settings = {...this.options, ...this.options.breakpoints[breakpoint]};
		const peek = typeof settings.peek === 'object' ? settings.peek.before + settings.peek.after : settings.peek * 2;
		// Match the existing Glide card widths, including fractional perView and peek.
		const width = (this.element.offsetWidth - peek - settings.gap * (settings.perView - 1)) / settings.perView;
		const wrapper = this.element.querySelector('.glide');
		const bounds = wrapper.getBoundingClientRect();
		const outer = this.element.getBoundingClientRect();
		this.element.style.setProperty('--slider-left', `${bounds.left - outer.left}px`);
		this.element.style.setProperty('--slider-right', `${outer.right - bounds.right}px`);
		this.list.style.gap = `${settings.gap}px`;
		this.slides.forEach(slide => { slide.style.width = `${width}px`; });
		this.slideStep = width + settings.gap;
		this.update();
	}

	scrollTo(left, smooth = false) {
		this.cancelSnap();
		this.track.scrollTo({left: Math.max(0, Math.min(this.maxScroll, left)),
			behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
	}

	scheduleSnap() {
		clearTimeout(this.snapTimer);
		if (this.isDragging || this.snapFrame || !window.matchMedia('(max-width: 767px)').matches) return;
		this.snapTimer = setTimeout(() => {
			// Keep the final position reachable without adding empty space after the last card.
			const positions = this.slides.map(slide => Math.min(this.maxScroll,
				slide.offsetLeft - this.slides[0].offsetLeft));
			const left = this.track.scrollLeft;
			const nearest = positions.reduce((closest, position) =>
				Math.abs(position - left) < Math.abs(closest - left) ? position : closest, 0);
			if (Math.abs(nearest - left) > 1) this.animateSnap(nearest);
		}, 220);
	}

	cancelSnap() {
		clearTimeout(this.snapTimer);
		cancelAnimationFrame(this.snapFrame);
		this.snapFrame = null;
	}

	animateSnap(target) {
		this.cancelSnap();
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.scrollTo(target);
			return;
		}
		const start = this.track.scrollLeft;
		const startedAt = performance.now();
		const frame = now => {
			const progress = Math.min(1, (now - startedAt) / 650);
			const eased = (1 - Math.cos(Math.PI * progress)) / 2;
			this.track.scrollTo({left: start + (target - start) * eased, behavior: 'instant'});
			this.snapFrame = progress < 1 ? requestAnimationFrame(frame) : null;
		};
		this.snapFrame = requestAnimationFrame(frame);
	}

	step(direction) {
		this.scrollTo(this.track.scrollLeft + direction * this.slideStep, true);
	}

	update() {
		const max = this.maxScroll;
		const progress = max ? Math.max(0, Math.min(1, this.track.scrollLeft / max)) : 0;
		const width = Math.min(this.bar.clientWidth, Math.max(24, this.bar.clientWidth * this.track.clientWidth / this.track.scrollWidth));
		this.thumb.style.width = `${width}px`;
		this.thumb.style.transform = `translateX(${progress * (this.bar.clientWidth - width)}px)`;
		this.bar.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
		this.bar.setAttribute('aria-disabled', String(max <= 1));
		this.bar.tabIndex = max > 1 ? 0 : -1;
		this.previous.disabled = this.track.scrollLeft <= 1;
		this.next.disabled = this.track.scrollLeft >= max - 1;
		this.onScroll();
	}

	bindDrag(target, isBar) {
		let drag;
		let suppressClick = false;
		target.addEventListener('pointerdown', event => {
			if (event.button !== 0 || (!isBar && event.pointerType !== 'mouse')) return;
			this.isDragging = true;
			this.cancelSnap();
			suppressClick = false;
			this.scrollTo(this.track.scrollLeft);
			const thumbBounds = this.thumb.getBoundingClientRect();
			drag = {id: event.pointerId, x: event.clientX, left: this.track.scrollLeft,
				offset: event.target === this.thumb ? event.clientX - thumbBounds.left : thumbBounds.width / 2};
			if (isBar) {
				event.preventDefault();
				target.focus({preventScroll: true});
				target.setPointerCapture(event.pointerId);
				move(event);
			}
		});
		const move = event => {
			if (!drag || drag.id !== event.pointerId) return;
			if (isBar) {
				const travel = this.bar.clientWidth - this.thumb.offsetWidth;
				this.scrollTo(travel > 0 ? (event.clientX - this.bar.getBoundingClientRect().left - drag.offset) / travel * this.maxScroll : 0);
			} else if (Math.abs(event.clientX - drag.x) > 5 || suppressClick) {
				suppressClick = true;
				target.setPointerCapture(event.pointerId);
				this.scrollTo(drag.left - (event.clientX - drag.x));
			}
		};
		target.addEventListener('pointermove', move);
		const end = () => {
			if (!drag) return;
			drag = null;
			this.isDragging = false;
			this.scheduleSnap();
		};
		target.addEventListener('pointerup', end);
		target.addEventListener('pointercancel', end);
		target.addEventListener('lostpointercapture', end);
		target.addEventListener('pointerleave', () => { if (!suppressClick && !isBar) end(); });
		target.addEventListener('dragstart', event => event.preventDefault());
		target.addEventListener('click', event => {
			if (!suppressClick) return;
			event.preventDefault();
			event.stopPropagation();
			suppressClick = false;
		}, true);
	}

	onScroll() {
		this.slides.filter(slide => isInViewport(slide)).forEach(slide => slide.classList.add('is-animated'));
	}
}
